import { useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { BsBuilding, BsPlus } from 'react-icons/bs';

import TextInput from '#globalComponents/Forms/TextInput';
import Tag from '#globalComponents/Tag/Tag';
import { useDebouncedValue } from '#globalHooks/useDebouncedValue';
import { useOutsideClick } from '#globalHooks/useOutsideClick';

import { useCompanyNameSearch } from '../../models/companies/queries';
import { useTargetingVocabulary } from '../../models/targeting/queries';
import { MAX_LOOKALIKE_SEEDS, useAgenticSearchStore } from '../../stores/agenticSearchStore';

const NO_PROFILE_TITLE = 'Für diese Firma liegt noch kein Website-Profil vor, sie kann nicht als Beispielfirma dienen.';

// Similar companies are found through the website profile, so a company without one cannot be a seed.
const SearchResult = ({ company, onPick }) => {
	const hasProfile = company.hasProfile !== false;
	return (
		<button
			type="button"
			onClick={() => hasProfile && onPick(company)}
			aria-disabled={!hasProfile}
			title={hasProfile ? undefined : NO_PROFILE_TITLE}
			className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs ${
				hasProfile ? 'text-text hover:bg-bgBase-300' : 'cursor-not-allowed text-bgBase-700'
			}`}
		>
			<BsBuilding className="h-3 w-3 flex-shrink-0 text-bgBase-800" />
			<span className="truncate">{company.companyName}</span>
			{company.addressCity && <span className="flex-shrink-0 text-bgBase-800">{company.addressCity}</span>}
			{!hasProfile && <span className="ml-auto flex-shrink-0 italic">kein Website-Profil</span>}
		</button>
	);
};

SearchResult.propTypes = {
	company: PropTypes.shape({
		id: PropTypes.string.isRequired,
		companyName: PropTypes.string,
		addressCity: PropTypes.string,
		hasProfile: PropTypes.bool,
	}).isRequired,
	onPick: PropTypes.func.isRequired,
};

const SearchResults = ({ results, isLoading, onPick }) => (
	<div className="absolute bottom-full left-0 right-0 z-10 mb-1 max-h-48 overflow-y-auto rounded-card border border-bgBase-500 bg-white py-1 shadow-elevated">
		{isLoading && <div className="px-3 py-2 text-xs text-bgBase-800">Suche läuft …</div>}
		{!isLoading && results.length === 0 && (
			<div className="px-3 py-2 text-xs text-bgBase-800">Keine Firma gefunden</div>
		)}
		{results.map((company) => (
			<SearchResult key={company.id} company={company} onPick={onPick} />
		))}
	</div>
);

SearchResults.propTypes = {
	results: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })).isRequired,
	isLoading: PropTypes.bool,
	onPick: PropTypes.func.isRequired,
};

/**
 * Lets the user name up to five example companies; the chat passes them to the assistant, which
 * then searches for similar companies (lookalike search).
 */
const LookalikeSeedPicker = () => {
	const seeds = useAgenticSearchStore((s) => s.lookalikeSeeds);
	const addLookalikeSeed = useAgenticSearchStore((s) => s.addLookalikeSeed);
	const removeLookalikeSeed = useAgenticSearchStore((s) => s.removeLookalikeSeed);
	const [isOpen, setIsOpen] = useState(false);
	const [query, setQuery] = useState('');
	const containerRef = useRef(null);
	const debouncedQuery = useDebouncedValue(query);
	const { data: results = [], isFetching } = useCompanyNameSearch(debouncedQuery);
	const { data: vocabulary } = useTargetingVocabulary();
	const isFull = seeds.length >= MAX_LOOKALIKE_SEEDS;
	const showResults = isOpen && debouncedQuery.trim().length >= 2;

	useOutsideClick(containerRef, () => setIsOpen(false), isOpen);

	if (!vocabulary?.searchEnabled) return null;

	const handlePick = (company) => {
		addLookalikeSeed({ id: company.id, name: company.companyName });
		setQuery('');
	};

	return (
		<div ref={containerRef} className="relative space-y-1.5 border-t border-gray-200 bg-white px-3 py-2">
			<div className="flex flex-wrap items-center gap-1">
				<span className="text-xs font-medium text-bgBase-800">Beispielfirmen:</span>
				{seeds.map((seed) => (
					<Tag
						key={seed.id}
						label={seed.name}
						tone="neutral"
						size="xs"
						onRemove={() => removeLookalikeSeed(seed.id)}
					/>
				))}
				{!isFull && !isOpen && (
					<button
						type="button"
						onClick={() => setIsOpen(true)}
						className="inline-flex items-center gap-0.5 text-xs text-primary-600 hover:text-primary-700"
					>
						<BsPlus className="h-3.5 w-3.5" />
						{seeds.length === 0 ? 'Ähnliche Firmen finden' : 'Weitere'}
					</button>
				)}
			</div>
			{isOpen && !isFull && (
				<TextInput
					autoFocus
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
					placeholder="Firmenname suchen …"
					className="!h-8 text-xs"
				/>
			)}
			{showResults && !isFull && (
				<SearchResults
					results={results.filter((company) => !seeds.some((seed) => seed.id === company.id))}
					isLoading={isFetching}
					onPick={handlePick}
				/>
			)}
		</div>
	);
};

export default LookalikeSeedPicker;
